import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { LAST_QUARTER_PRESET } from '@app/storybook/components/datepicker/date-range-presets';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { OrderEditingService } from '@app/pages/order-editing/order-editing.service';
import { ShopService } from '@app/services/shops/shop.service';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  Subject,
  switchMap,
} from 'rxjs';
import {
  ADDRESS_CORRECTION_STATUS_MAP,
  AddressCorrectionHistoryItem,
  AddressCorrectionHistoryQuery,
  AddressCorrectionStatusTab,
  PageInfo,
} from './address-validation-flagged-orders.types';
import { FLAGGED_ORDERS_COLUMNS } from './address-validation-flagged-orders.table-config';
import { PaginationCursorAction } from '@app/storybook/layouts/components/footer/pagination/pagination.component';

const STATUS_KEYS = [
  AddressCorrectionStatusTab.CORRECTED,
  AddressCorrectionStatusTab.KEPT_ORIGINAL,
  AddressCorrectionStatusTab.PENDING,
  AddressCorrectionStatusTab.EXPIRED,
] as const;
const ROW_HEIGHT = 48;
const HEADER_ROW_HEIGHT = 48;
const PAGINATION_HEIGHT = 48;
const DEFAULT_PAGE_SIZE = 5;

const STATUS_TABS = [
  { baseLabel: 'All', value: AddressCorrectionStatusTab.ALL },
  { baseLabel: 'Corrected', value: AddressCorrectionStatusTab.CORRECTED },
  { baseLabel: 'Pending', value: AddressCorrectionStatusTab.PENDING },
  { baseLabel: 'Kept Original', value: AddressCorrectionStatusTab.KEPT_ORIGINAL },
  { baseLabel: 'Expired', value: AddressCorrectionStatusTab.EXPIRED },
];

function computeRangeLabel(rowCount: number, currentPage: number, pageSize: number): string {
  if (rowCount === 0) return '';
  const start = (currentPage - 1) * pageSize + 1;
  const end = start + rowCount - 1;
  return `${start}-${end}`;
}

@Component({
  selector: 'address-validation-flagged-orders',
  standalone: false,
  templateUrl: './address-validation-flagged-orders.component.html',
  styleUrl: './address-validation-flagged-orders.component.scss',
})
export class AddressValidationFlaggedOrdersComponent implements OnInit, AfterViewInit {
  @ViewChild('toolbarRef', { static: true }) toolbarTemplate: TemplateRef<void>;

  readonly statusMap = ADDRESS_CORRECTION_STATUS_MAP;
  readonly columns = FLAGGED_ORDERS_COLUMNS;
  readonly searchControl = new FormControl<string>('');
  readonly defaultRange = LAST_QUARTER_PRESET;
  readonly shopCreated = inject(ShopService).shopCreatedSignal;

  pageSize$ = new BehaviorSubject<number>(DEFAULT_PAGE_SIZE);
  pageSize = toSignal(this.pageSize$);
  readonly counts = signal<Record<string, number | null>>({
    [AddressCorrectionStatusTab.ALL]: null,
    [AddressCorrectionStatusTab.CORRECTED]: null,
    [AddressCorrectionStatusTab.KEPT_ORIGINAL]: null,
    [AddressCorrectionStatusTab.PENDING]: null,
    [AddressCorrectionStatusTab.EXPIRED]: null,
  });

  readonly statusTabs = computed(() => {
    const countsValue = this.counts();
    return STATUS_TABS.map((tab) => ({
      ...tab,
      label: countsValue[tab.value] != null ? `${tab.baseLabel} (${countsValue[tab.value]})` : tab.baseLabel,
    }));
  });

  readonly activeStatusTab = signal<AddressCorrectionStatusTab>(AddressCorrectionStatusTab.ALL);
  readonly currentPage = signal(1);

  readonly rows = signal<AddressCorrectionHistoryItem[]>([]);

  readonly rangeLabel = computed(() => computeRangeLabel(this.rows().length, this.currentPage(), this.pageSize()));
  loading = false;
  countsLoading = false;
  pageInfo: PageInfo | null = null;

  private dateRange: { startDate?: string; endDate?: string } = {};
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef);
  private shopService = inject(ShopService);
  private loadRows$ = new Subject<{ first?: number; last?: number; cursor?: string } | void>();
  private loadCounts$ = new Subject<void>();

  constructor(private orderEditingService: OrderEditingService) {}

  ngOnInit(): void {
    this.subscribeToLoadRows();
    this.subscribeToLoadCounts();
    this.subscribeToSearch();
    this.subscribeToPageSizeChanges();
  }

  ngAfterViewInit(): void {
    this.observeTableHeight();
  }

  getShopifyOrderUrl(orderId: number): string {
    const shopName = this.shopService.shopSignal()?.name;
    return shopName ? `https://${shopName}/admin/orders/${orderId}` : '';
  }

  onStatusTabChange(value: AddressCorrectionStatusTab): void {
    this.activeStatusTab.set(value);
    this.resetPagination();
    this.loadRows$.next();
  }

  onDateRangeChange(event: { start: string; end: string }): void {
    this.dateRange = {
      startDate: new Date(+event.start).toISOString(),
      endDate: new Date(+event.end).toISOString(),
    };
    this.resetPagination();
    this.loadRows$.next();
    this.loadCounts$.next();
  }

  onCursorPageChange(action: PaginationCursorAction): void {
    switch (action) {
      case 'first':
        this.currentPage.set(1);
        this.loadRows$.next();
        break;
      case 'prev':
        this.currentPage.update((p) => p - 1);
        this.loadRows$.next({ last: this.pageSize(), cursor: this.pageInfo?.startCursor });
        break;
      case 'next':
        this.currentPage.update((p) => p + 1);
        this.loadRows$.next({ first: this.pageSize(), cursor: this.pageInfo?.endCursor });
        break;
      case 'last': {
        const activeCount = this.counts()[this.activeStatusTab()] ?? 0;
        this.currentPage.set(Math.ceil(activeCount / this.pageSize()));
        this.loadRows$.next({ last: this.pageSize() });
        break;
      }
    }
  }

  private subscribeToSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        filter((value) => value.length >= 4 || value.length === 0),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.resetPagination();
        this.loadRows$.next();
        this.loadCounts$.next();
      });
  }

  private subscribeToLoadRows(): void {
    this.loadRows$
      .pipe(
        switchMap((paginationParams) => {
          this.loading = true;
          const params: AddressCorrectionHistoryQuery = {
            ...this.buildBaseParams(),
            first: this.pageSize(),
            ...paginationParams,
          };
          return this.orderEditingService
            .loadAddressCorrectionHistory$(params)
            .pipe(finalize(() => (this.loading = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.rows.set(response.items);
          this.pageInfo = response.pageInfo;
        },
      });
  }

  private subscribeToLoadCounts(): void {
    this.loadCounts$
      .pipe(
        switchMap(() => {
          this.countsLoading = true;
          const baseParams = this.buildBaseParams();
          const countWithoutStatus = { ...baseParams };
          delete countWithoutStatus.status;

          const countRequests: Record<
            string,
            ReturnType<typeof this.orderEditingService.loadAddressCorrectionCount$>
          > = {
            [AddressCorrectionStatusTab.ALL]: this.orderEditingService.loadAddressCorrectionCount$(countWithoutStatus),
          };

          for (const status of STATUS_KEYS) {
            countRequests[status] = this.orderEditingService.loadAddressCorrectionCount$({
              ...countWithoutStatus,
              status,
            });
          }

          return forkJoin(countRequests).pipe(finalize(() => (this.countsLoading = false)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          this.counts.set(Object.fromEntries(Object.entries(result).map(([key, val]) => [key, val.count])));
        },
      });
  }

  private subscribeToPageSizeChanges(): void {
    this.pageSize$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetPagination();
      this.loadRows$.next();
    });
  }

  private observeTableHeight(): void {
    const stretchEl = this.elementRef.nativeElement.querySelector('.table-stretch');
    if (!stretchEl) return;

    const resizeObserver = new ResizeObserver(() => {
      const availableHeight = stretchEl.clientHeight - HEADER_ROW_HEIGHT - PAGINATION_HEIGHT;
      const calculated = Math.floor(availableHeight / ROW_HEIGHT);
      this.pageSize$.next(Math.max(calculated, DEFAULT_PAGE_SIZE));
      resizeObserver.disconnect();
    });
    resizeObserver.observe(stretchEl);
  }

  private resetPagination(): void {
    this.currentPage.set(1);
    this.pageInfo = null;
  }

  private buildBaseParams(): Omit<AddressCorrectionHistoryQuery, 'cursor' | 'first' | 'last'> {
    const params: Omit<AddressCorrectionHistoryQuery, 'cursor' | 'first' | 'last'> = {};
    if (this.dateRange.startDate) params.startDate = this.dateRange.startDate;
    if (this.dateRange.endDate) params.endDate = this.dateRange.endDate;

    const searchValue = this.searchControl.value;
    if (searchValue?.length >= 4) {
      const parsed = parseInt(searchValue, 10);
      if (!isNaN(parsed)) params.orderNumber = parsed;
    }

    if (this.activeStatusTab() !== AddressCorrectionStatusTab.ALL) params.status = this.activeStatusTab();
    return params;
  }
}
